import { useMutation } from "@apollo/client";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  AiAssistantChatMessageFragment,
  AiAssistantChatRole,
  AiAssistantSubscriptionVariables,
} from "gql/graphql";
import { START_AI_ASSISTANT_CHAT } from "@/modules/ai/features/ask-ai-assistant/api/start-ai-assistant-chat";
import { useAiAssistantAnswer } from "@/modules/ai/features/ask-ai-assistant/model/use-ai-assistant-answer";

/**
 * Разговор с ассистентом: заводит разговор перед первым вопросом, копит переписку и отдаёт ответ,
 * который печатается.
 *
 * Разговор живёт в памяти бэкенда и вытесняется через два часа, а перезапуск бэкенда уносит его
 * весь: id после отказа забывается, следующий вопрос заводит новый разговор.
 */
export function useAiAssistantChat() {
  const [chatId, setChatId] = useState<string>();
  const [messages, setMessages] = useState<AiAssistantChatMessageFragment[]>(
    [],
  );

  const [request, setRequest] = useState<AiAssistantSubscriptionVariables>();

  const [startChat, { loading: starting }] = useMutation(
    START_AI_ASSISTANT_CHAT,
  );

  const addMessage = (message: AiAssistantChatMessageFragment) => {
    setMessages((messages) => [...messages, message]);
  };

  const { answer, stage, answering } = useAiAssistantAnswer({
    request,

    onComplete: (answer) => {
      addMessage(answer);
      setRequest(undefined);
    },

    onError: ({ started }) => {
      setRequest(undefined);

      // Отказ до первого сообщения — это в том числе «разговор не найден»: он мог не пережить
      // перезапуск бэкенда, и с тем же id продолжать нечего. Оборвавшийся на середине ответ
      // разговор не роняет.
      if (!started) {
        setChatId(undefined);
      }
    },
  });

  const ask = async (question: string) => {
    addMessage({
      id: uuidv4(),
      role: AiAssistantChatRole.User,
      text: question,
      toolCalls: [],
    });

    if (chatId) {
      setRequest({ question, chatId });
      return;
    }

    const { data } = await startChat();
    const startedChatId = data?.startAiAssistantChat.id;

    setChatId(startedChatId);
    setRequest({ question, chatId: startedChatId });
  };

  return {
    messages,
    answer,
    stage,
    ask,
    answering: answering || starting,
  };
}
